import React, { useState } from 'react'
import {
    ChatBubbleBottomCenterTextIcon,
    ListBulletIcon,
    PlusIcon,
    SparklesIcon,
    TrashIcon,
} from '@heroicons/react/24/outline'
import {
    Control,
    UseFormGetValues,
    UseFormRegister,
    UseFormSetValue,
    useWatch,
} from 'react-hook-form'
import InputField from 'components/styledFormElements/InputField'
import { useNotificationBar } from 'context/notificationContext'
import { isRichTextEmpty } from 'components/articleEditor/helpers'
import { getErrorResponse } from 'services/util'
import { unwrapResponse, useGenerateArticleSeo } from 'hooks/article'

const CONTENT_TYPES = [
    { value: 'BlogPosting', label: 'Blog post' },
    { value: 'Article', label: 'Article' },
    { value: 'NewsArticle', label: 'News article' },
]

interface ArticleSeoSectionProps {
    section: 'search' | 'enrichment'
    articleId?: number
    control: Control<any>
    register: UseFormRegister<any>
    setValue: UseFormSetValue<any>
    getValues: UseFormGetValues<any>
    errors: any
    disabled?: boolean
}

const fieldClass =
    'w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-slate-700'

const ArticleSeoSection = ({
    section,
    articleId,
    control,
    register,
    setValue,
    getValues,
    errors,
    disabled,
}: ArticleSeoSectionProps) => {
    const notification = useNotificationBar()
    const generateSeo = useGenerateArticleSeo()
    const [generating, setGenerating] = useState(false)
    const takeaways = useWatch({ control, name: 'keyTakeaways' }) || []
    const faqs = useWatch({ control, name: 'faqs' }) || []
    const [seoTitle, metaDescription, title, slug] = useWatch({
        control,
        name: ['seoTitle', 'metaDescription', 'title', 'slug'],
    })

    const setList = (name: 'keyTakeaways' | 'faqs', next: any[]) => {
        setValue(name, next, { shouldDirty: true, shouldValidate: true })
    }

    const applySuggestions = (suggestions: Record<string, any>) => {
        const text = (key: string, field: string) => {
            if (typeof suggestions[key] === 'string') {
                setValue(field, suggestions[key], {
                    shouldDirty: true,
                    shouldValidate: true,
                })
            }
        }
        text('seo_title', 'seoTitle')
        text('meta_description', 'metaDescription')
        text('focus_keyword', 'focusKeyword')
        text('search_intent', 'searchIntent')
        text('direct_answer', 'directAnswer')
        text('geo_summary', 'aiSummary')
        if (suggestions.schema_type)
            setValue('contentType', suggestions.schema_type, {
                shouldDirty: true,
            })
        if (Array.isArray(suggestions.key_takeaways))
            setList('keyTakeaways', suggestions.key_takeaways)
        if (Array.isArray(suggestions.faqs)) {
            setList(
                'faqs',
                suggestions.faqs.length
                    ? suggestions.faqs
                    : [{ question: '', answer: '' }]
            )
        }
        if (Array.isArray(suggestions.related_articles)) {
            setValue(
                'relatedArticles',
                suggestions.related_articles.join(', '),
                { shouldDirty: true, shouldValidate: true }
            )
        }
    }

    const onGenerate = async () => {
        const title = getValues('title')?.trim()
        const content = getValues('content_html') || ''
        if (!title || isRichTextEmpty(content)) {
            notification.push({
                title: 'Add content first',
                type: 'error',
                message:
                    'Enter a title and article content before generating SEO.',
            })
            return
        }
        if (content.length > 30000) {
            notification.push({
                title: 'Content is too long',
                type: 'error',
                message:
                    'SEO generation supports up to 30,000 characters of content.',
            })
            return
        }

        setGenerating(true)
        try {
            const response = await generateSeo.mutateAsync({
                ...(articleId ? { article_id: articleId } : {}),
                title,
                short_description: getValues('short_description') || '',
                author: getValues('author') || '',
                content_html: content,
                language: 'English',
            })
            const result = unwrapResponse(response)
            applySuggestions(result?.suggestions || {})
            notification.push({
                title: 'SEO suggestions ready',
                type: 'success',
                message:
                    'Review the suggestions, then save the article. They are not saved automatically.',
            })
        } catch (error) {
            notification.push(getErrorResponse(error))
        } finally {
            setGenerating(false)
        }
    }

    return (
        <>
            {section === 'search' && (
                <>
                    <div className="article-seo-generate">
                        <span className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                            Start with AI suggestions,
                            <br />
                            then make them your own.
                        </span>
                        <button
                            type="button"
                            onClick={onGenerate}
                            disabled={disabled || generating}
                            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs font-medium text-blue-700 hover:bg-blue-100 disabled:opacity-50 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                        >
                            <SparklesIcon className="h-4 w-4" />
                            {generating ? 'Generating...' : 'Generate SEO'}
                        </button>
                    </div>

                    <div className="article-counted-field relative">
                        <span
                            className="article-field-count"
                            title="Suggested search title length: 60 characters"
                        >
                            {(seoTitle || '').length}/60 suggested
                        </span>
                        <InputField
                            aria-label="SEO title"
                            label="SEO title"
                            helpText="Title shown in search results. Required before publishing."
                            placeholder="Search result title"
                            type="text"
                            maxLength={255}
                            register={register('seoTitle')}
                            error={errors?.seoTitle?.message as string}
                        />
                    </div>
                    <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <InputField
                            aria-label="Focus keyword"
                            label="Focus keyword"
                            helpText="Main phrase this article should rank for."
                            placeholder="Main search phrase"
                            type="text"
                            maxLength={255}
                            register={register('focusKeyword')}
                            error={errors?.focusKeyword?.message as string}
                        />
                        <InputField
                            aria-label="Search intent"
                            label="Search intent"
                            helpText="What the reader is trying to find."
                            placeholder="Learn how this topic works"
                            type="text"
                            maxLength={500}
                            register={register('searchIntent')}
                            error={errors?.searchIntent?.message as string}
                        />
                    </div>
                    <div className="mt-4">
                        <div className="flex flex-col">
                            <label
                                htmlFor="contentType"
                                className="mb-2 text-sm font-medium text-gray-700 dark:text-gray-300"
                            >
                                Content type
                            </label>
                            <select
                                id="contentType"
                                {...register('contentType')}
                                className={fieldClass}
                            >
                                {CONTENT_TYPES.map((option) => (
                                    <option
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            {errors?.contentType && (
                                <span className="mt-1 text-xs text-red-500">
                                    {errors.contentType.message as string}
                                </span>
                            )}
                        </div>
                    </div>

                    <div className="article-counted-field relative mt-4">
                        <span
                            className="article-field-count"
                            title="Suggested description length: 160 characters"
                        >
                            {(metaDescription || '').length}/160 suggested
                        </span>
                        <InputField
                            aria-label="Meta description"
                            label="Meta description"
                            helpText="Short summary shown under the title in search results. Required before publishing."
                            placeholder="Search result description"
                            isTextarea
                            row={3}
                            maxLength={500}
                            register={register('metaDescription')}
                            error={errors?.metaDescription?.message as string}
                        />
                    </div>
                    <div
                        className="article-search-preview"
                        aria-label="Search result preview"
                    >
                        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                            Search preview
                        </p>
                        <p className="truncate text-xs text-slate-500 dark:text-slate-400">
                            /articles/{slug || 'your-article-url'}
                        </p>
                        <p className="mt-1 break-words text-base font-medium leading-6 text-blue-700 dark:text-blue-400">
                            {seoTitle || title || 'Your article title'}
                        </p>
                        <p className="mt-1 break-words text-xs leading-5 text-slate-600 dark:text-slate-300">
                            {metaDescription ||
                                'Add a meta description to give readers a helpful preview of your article.'}
                        </p>
                    </div>

                    <div className="mt-4">
                        <InputField
                            aria-label="Direct answer"
                            label="Direct answer"
                            helpText="One clear answer a reader or search engine can quote."
                            placeholder="The short answer to the article's main question"
                            isTextarea
                            row={3}
                            register={register('directAnswer')}
                            error={errors?.directAnswer?.message as string}
                        />
                    </div>
                </>
            )}

            {section === 'enrichment' && (
                <div className="article-enrichment-space">
                    <div className="article-ai-summary-card">
                        <div className="article-enrichment-heading">
                            <span className="article-enrichment-icon">
                                <SparklesIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </span>
                            <div>
                                <h4>AI-ready summary</h4>
                                <p>
                                    Give answer engines a concise,
                                    self-contained overview.
                                </p>
                            </div>
                        </div>
                        <InputField
                            aria-label="AI summary"
                            label="AI summary"
                            helpText="A concise summary written for AI answer engines."
                            placeholder="Summary of the article"
                            isTextarea
                            row={4}
                            register={register('aiSummary')}
                            error={errors?.aiSummary?.message as string}
                        />
                    </div>

                    <div className="article-enrichment-block">
                        <div className="article-enrichment-heading article-enrichment-heading--compact">
                            <span className="article-enrichment-icon">
                                <ListBulletIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-3">
                                    <h4>Key takeaways</h4>
                                    <span className="article-enrichment-count">
                                        {
                                            takeaways.filter((item: string) =>
                                                item?.trim()
                                            ).length
                                        }
                                        /5
                                    </span>
                                </div>
                                <p>
                                    Add 3 to 5 concise points, or leave this
                                    section empty.
                                </p>
                            </div>
                            <button
                                type="button"
                                disabled={takeaways.length >= 5}
                                onClick={() =>
                                    setList('keyTakeaways', [...takeaways, ''])
                                }
                                className="inline-flex h-8 items-center gap-1 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                                Add takeaway
                            </button>
                        </div>
                        <div className="article-takeaway-list">
                            {takeaways.map((_: string, index: number) => (
                                <div
                                    key={`takeaway-${index}`}
                                    className="article-takeaway-row"
                                >
                                    <span
                                        className="article-takeaway-number"
                                        aria-hidden="true"
                                    >
                                        {index + 1}
                                    </span>
                                    <input
                                        {...register(`keyTakeaways.${index}`)}
                                        aria-label={`Takeaway ${index + 1}`}
                                        placeholder={`Takeaway ${index + 1}`}
                                        maxLength={500}
                                        className={fieldClass}
                                    />
                                    {takeaways.length > 1 && (
                                        <button
                                            type="button"
                                            title="Delete"
                                            onClick={() =>
                                                setList(
                                                    'keyTakeaways',
                                                    takeaways.filter(
                                                        (__, item) =>
                                                            item !== index
                                                    )
                                                )
                                            }
                                            aria-label={`Remove takeaway ${
                                                index + 1
                                            }`}
                                            className="article-enrichment-remove"
                                        >
                                            <TrashIcon className="h-4 w-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                        {errors?.keyTakeaways?.message && (
                            <span className="mt-1 block text-xs text-red-500">
                                {errors.keyTakeaways.message as string}
                            </span>
                        )}
                    </div>

                    <div className="article-enrichment-block">
                        <div className="article-enrichment-heading article-enrichment-heading--compact">
                            <span className="article-enrichment-icon">
                                <ChatBubbleBottomCenterTextIcon
                                    className="h-4 w-4"
                                    aria-hidden="true"
                                />
                            </span>
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-3">
                                    <h4>Frequently asked questions</h4>
                                    <span className="article-enrichment-count">
                                        {
                                            faqs.filter(
                                                (item: any) =>
                                                    item.question?.trim() ||
                                                    item.answer?.trim()
                                            ).length
                                        }
                                        /20
                                    </span>
                                </div>
                                <p>
                                    Provide direct answers to the questions
                                    readers are most likely to ask.
                                </p>
                            </div>
                            <button
                                type="button"
                                disabled={faqs.length >= 20}
                                onClick={() =>
                                    setList('faqs', [
                                        ...faqs,
                                        { question: '', answer: '' },
                                    ])
                                }
                                className="inline-flex h-8 items-center gap-1 rounded-md bg-blue-600 px-3 text-xs font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                <PlusIcon className="h-3.5 w-3.5" />
                                Add question
                            </button>
                        </div>
                        <div className="article-faq-list">
                            {faqs.map((_: any, index: number) => (
                                <div
                                    key={`faq-${index}`}
                                    className="article-faq-card"
                                >
                                    <div className="article-faq-card-header">
                                        <span>Question {index + 1}</span>
                                        {faqs.length > 1 && (
                                            <button
                                                type="button"
                                                title="Delete"
                                                onClick={() =>
                                                    setList(
                                                        'faqs',
                                                        faqs.filter(
                                                            (__, item) =>
                                                                item !== index
                                                        )
                                                    )
                                                }
                                                aria-label={`Remove question ${
                                                    index + 1
                                                }`}
                                                className="article-enrichment-remove"
                                            >
                                                <TrashIcon className="h-4 w-4" />
                                            </button>
                                        )}
                                    </div>
                                    <input
                                        {...register(`faqs.${index}.question`)}
                                        aria-label={`FAQ ${index + 1} question`}
                                        placeholder="Question"
                                        maxLength={500}
                                        className={fieldClass}
                                    />
                                    <textarea
                                        {...register(`faqs.${index}.answer`)}
                                        aria-label={`FAQ ${index + 1} answer`}
                                        rows={2}
                                        placeholder="Answer"
                                        maxLength={5000}
                                        className={`${fieldClass} mt-2`}
                                    />
                                </div>
                            ))}
                        </div>
                        {errors?.faqs?.message && (
                            <span className="mt-1 block text-xs text-red-500">
                                {errors.faqs.message as string}
                            </span>
                        )}
                    </div>
                </div>
            )}
        </>
    )
}

export default ArticleSeoSection
